import React, { useRef, useState } from 'react';
import s from './s.modules.less';
import { Button, message, Modal, Spin } from 'antd';
import EditPaymentModal from 'components/EditPaymentModal';
import { EKanmonUserState } from 'types/common';
import { getKanmonUiView } from 'utils/kanmonUi';
import { getUserId } from 'utils/localstore';
import commonS from 'styles/common.module.less';

import ProviderStore from 'store/Provider';
import useGetPPAPaymentInfo from 'hooks/useGetPPAPaymentInfo';
import useKanmonConnect from 'hooks/useKanmonConnect';
import BillingPortalButton from 'components/BillingPortalButton';
import FinancialsLandingPage from '../FinancialsLandingPage';

import { kanmonPaymentActivate } from 'api/financials';
import PaybackTermModal from '../PaybackTermModal';
import useKanmonProducts from 'hooks/useKanmonProducts';

type Props = {
    open?: boolean;
    onClose?: () => void;
    price?: number;
    onPaymentInfoChange?: () => void;
    onSuccess?: () => void;
    header?: React.ReactNode;
    title?: string;
};

const PaymentSettingModal = ({ open, onClose, onPaymentInfoChange, onSuccess }: Props) => {
    const [paymentInfo] = useGetPPAPaymentInfo();
    const [autoShowKanmonModal, setAutoShowKanmonModal] = React.useState(false);
    const [selectType, setSelectType] = useState<'card' | 'paylater' | null>(null);
    const [loading, setLoading] = useState(false);
    const [kanmonConnect, kanmonInfo, loadingKanmonInfo, refetchKanmonInfo, ui] = useKanmonConnect();
    const [showEdit, setShowEdit] = useState(false);
    const [showPaylaterModal, setShowPaylaterModal] = useState(false);
    const [showPaybackTermModal, setShowPaybackTermModal] = useState(false);
    const [products, loadingProducts, refetchProducts, productsError] = useKanmonProducts();
    const showUpdateButton = products.length > 0;
    const productsStatus = productsError ? 'error' : 'ready';
    const view = getKanmonUiView(ui, !!kanmonInfo?.kanmonBusinessId, products, loadingProducts ? 'loading' : productsStatus);
    const [getProvider] = ProviderStore.useStore();
    const providerId = getProvider('data')?.id;
    const submitting = useRef(false);

    React.useEffect(() => {
        if (open) refetchProducts();
    }, [open, refetchProducts]);
    React.useEffect(() => {
        submitting.current = false;
        setLoading(false);
    }, [providerId]);
    React.useEffect(() => {
        if (autoShowKanmonModal && kanmonConnect) {
            setAutoShowKanmonModal(false);
            setShowPaylaterModal(false);
            kanmonConnect.show();
        }
    }, [autoShowKanmonModal, kanmonConnect]);

    const handleClickPaylater = () => {
        setShowPaylaterModal(true);
    };

    React.useEffect(() => {
        const status = kanmonInfo?.paymentActiveStatus;
        if (status === 'A') setSelectType('paylater');
        else if (status === 'D') setSelectType('card');
        else setSelectType(null);
    }, [kanmonInfo]);

    const handlePaymentMethodChange = async (type: 'card' | 'paylater') => {
        if (selectType === type || submitting.current) return;
        if (type === 'paylater' && !view.canSelectPayLater) {
            message.info(productsError || (ui.userState === EKanmonUserState.SERVICING
                ? 'Your financing product is not available yet. Please refresh and try again.' : view.detailText));
            return;
        }
        if (!providerId) return;
        submitting.current = true;
        setLoading(true);
        try {
            const res = await kanmonPaymentActivate(type === 'paylater' ? 'A' : 'D');
            if ((ProviderStore.get('data')?.id ?? getUserId()) !== providerId) return;
            if (res.error) {
                message.error('Unable to update your payment method. Please try again.');
                return;
            }
            message.success(type === 'paylater' ? 'Klarity Pay Later activated successfully' : 'Stripe is now your default payment method');
            setSelectType(type);
            refetchKanmonInfo();
            onSuccess?.();
        } catch {
            if ((ProviderStore.get('data')?.id ?? getUserId()) === providerId) message.error('Unable to update your payment method. Please try again.');
        } finally {
            if ((ProviderStore.get('data')?.id ?? getUserId()) === providerId) {
                submitting.current = false;
                setLoading(false);
            }
        }
    };

    let cardLabel = '';
    let brand = paymentInfo?.brand || paymentInfo?.bankName;
    if (!brand) {
        try {
            const detailJson = JSON.parse(paymentInfo?.detail || '{}');
            brand = detailJson.displayBrand;
        } catch {
            // Keep the fallback label when payment metadata cannot be parsed.
        }
    }
    let isCard = true;
    if (paymentInfo && paymentInfo.type === 'card') {
        cardLabel = 'Card (via Stripe)';
        isCard = true;
        // paymentLabel = `Card ending in ${paymentInfo.last4}`;
    } else if (paymentInfo && paymentInfo.type === 'us_bank_account') {
        // paymentLabel = `Bank account ending in ${paymentInfo.last4}`;
        cardLabel = 'Bank account (via Stripe)';
        isCard = false;
    }
    const handleKanmonConnect = () => {
        if (view.primaryAction === 'apply') {
            handleClickPaylater();
            onClose?.();
        } else if (view.primaryAction === 'retry' || !kanmonConnect) {
            refetchKanmonInfo();
            refetchProducts();
        } else {
            kanmonConnect.show();
        }
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                width={550}
                footer={null}
                title="Payment method settings"
            >
                <Spin spinning={loading}>
                    <div className={`${s.wrap} ${selectType === 'paylater' ? s.wrapRevert : ''}`}>
                        <div
                            onClick={() => {
                                handlePaymentMethodChange('card');
                            }}
                            className={`${s.item} ${selectType === 'card' ? s.activeItem : ''}`}
                        >
                            <div className={s.left}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 10H21M7 15H8M12 15H13M6 19H18C19.6569 19 21 17.6569 21 16V8C21 6.34315 19.6569 5 18 5H6C4.34315 5 3 6.34315 3 8V16C3 17.6569 4.34315 19 6 19Z" stroke="#0E62F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className={s.right}>
                                {
                                    selectType === 'card' && (
                                        <div className={s.tipsActive}>
                                            Current payment method
                                        </div>
                                    )
                                }
                                {
                                    selectType !== 'card' && (
                                        <div className={s.tips}>
                                            Available payment method
                                        </div>
                                    )
                                }
                                <div className={s.title}>{cardLabel}</div>
                                {
                                    isCard && (
                                        <div className={s.content}>
                                            {brand && <div className={s.brand}>{brand}</div>}
                                            ****
                                            <div className={s.last4}>{paymentInfo?.last4}</div>
                                            <div className={s.default}>
                                                Default
                                            </div>
                                            <div className={s.expires}>
                                                Expires on {paymentInfo?.expMonth}/{paymentInfo?.expYear}
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    !isCard && (
                                        <div className={`${s.content} ${s.bankContent}`}>
                                            {brand && <div className={s.brand}>{brand}</div>}
                                            <div className={s.cInfo}>
                                                <div className={s.c1}>{brand} bank</div>
                                                <div className={s.c2}>
                                                    checking
                                                    ****
                                                    <div className={s.last4}>{paymentInfo?.last4}</div>
                                                </div>
                                            </div>
                                            <div className={s.default}>
                                                Default
                                            </div>
                                        </div>
                                    )
                                }

                                <div className={s.tips}>
                                    Manage your payment details directly in Stripe.
                                </div>
                                <div className={s.btn} onClick={(event) => event.stopPropagation()}>
                                    <BillingPortalButton size="large" type="default" block style={{ background: '#fff' }} hideIcon text="Manage in Stripe" />
                                </div>
                                {
                                    selectType !== 'card' && (
                                        <div className={s.tips2}>Switching to Stripe will make it your default payment method.</div>
                                    )
                                }
                            </div>
                        </div>

                        <div
                            onClick={() => {
                                handlePaymentMethodChange('paylater');
                            }}
                            className={`${s.item} ${selectType === 'paylater' ? s.activeItem : ''}`}
                        >
                            <div className={s.left}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M13 10V3L4 14H11L11 21L20 10L13 10Z" stroke="#0E62F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className={s.right}>
                                {
                                    selectType === 'paylater' && (
                                        <div className={s.tipsActive}>
                                            Current payment method
                                        </div>
                                    )
                                }
                                {
                                    selectType !== 'paylater' && (
                                        <div className={s.tips}>
                                            Available payment method
                                        </div>
                                    )
                                }
                                <div className={s.title}>
                                    Klarity Pay Later
                                    <span className={s.recommend}>Recommended</span>
                                </div>

                                <div className={s.tips}>
                                    {view.detailText}
                                </div>
                                <div className={s.btn} onClick={(event) => event.stopPropagation()}>
                                    <Button
                                        className={view.showInputRequired ? s.actionRequireBtn : ''}
                                        size="large"
                                        block
                                        type="primary"
                                        loading={loadingKanmonInfo || ui.status === 'loading'}
                                        onClick={handleKanmonConnect}
                                    >
                                        {view.ctaText}
                                    </Button>
                                </div>
                                {ui.userState === EKanmonUserState.SERVICING && !view.canSelectPayLater && (
                                    <div className={s.btn} onClick={(event) => event.stopPropagation()}>
                                        <span role="status">{productsError || (loadingProducts ? 'Loading financing products…' : 'Financing product is not available yet.')}</span>
                                        <Button loading={loadingProducts} onClick={() => refetchProducts()}>Refresh financing</Button>
                                    </div>
                                )}
                                {
                                    showUpdateButton && (
                                        <div className={s.btn} onClick={(event) => event.stopPropagation()}>
                                            <Button
                                                onClick={() => {
                                                    onClose?.();
                                                    setShowPaybackTermModal(true);
                                                }}
                                                size="large"
                                                block
                                            >
                                                Update invoice payback term
                                            </Button>
                                        </div>
                                    )
                                }

                                {
                                    selectType !== 'paylater' && !view.canSelectPayLater && (
                                        <div className={s.tips2}>
                                            You can select Klarity Pay Later after your financing is ready.
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </Spin>
            </Modal>

            <EditPaymentModal
                open={showEdit}
                onClose={() => setShowEdit(false)}
                onUpdatePaymentInfo={() => {
                    setShowEdit(false);
                    onPaymentInfoChange?.();
                }}
                hideBank
            />
            <Modal
                width="90%"
                open={showPaylaterModal}
                className={`${commonS.modalFixHeightWrap} ${s.modalWrap}`}
                title="Grow your practice faster with Klarity Pay Later (Powered by Kanmon)"
                onCancel={() => {
                    setShowPaylaterModal(false);
                }}
                footer={null}
                destroyOnClose
            >
                <div className={s.modalWrap}>
                    <FinancialsLandingPage
                        onClickedApplyNow={() => {
                            refetchKanmonInfo();
                            setAutoShowKanmonModal(true);
                        }}
                        doShowKanmonModal={() => {
                            setShowPaylaterModal(false);
                            if (kanmonConnect) {
                                kanmonConnect.show();
                            } else {
                                refetchKanmonInfo();
                            }
                        }}
                        isModal
                    />
                </div>
            </Modal>
            <PaybackTermModal
                open={showPaybackTermModal}
                onCancel={() => setShowPaybackTermModal(false)}
                onSuccess={() => {
                    setShowPaybackTermModal(false);
                }}
            />
        </>
    );
};

export default PaymentSettingModal;
